import { useEffect, useState } from "react";
import { useRoboBallAssetUrl } from "@/lib/roboBall";

/**
 * Shared Robo-Ball avatar image with a 400 ms opacity cross-fade when the
 * dynamic jersey asset changes. No transform / no width / no height
 * animation → zero reflow, so every consumer's text bounding boxes stay
 * immutable (Phase 11 layout invariant).
 *
 * Consumers pass their own `className` (sizing / rounding) and an optional
 * `fallback` node rendered if the <img> onError fires.
 */
export default function RoboBallImage({
  className = "",
  alt = "",
  fallback = null,
  imgClassName = "",
  style = {},
  ...rest
}) {
  const src = useRoboBallAssetUrl();
  const [displayedSrc, setDisplayedSrc] = useState(src);
  const [opacity, setOpacity] = useState(1);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (src === displayedSrc) return;
    setOpacity(0);
    const t = setTimeout(() => {
      setDisplayedSrc(src);
      setFailed(false); // give the new asset a fresh chance to load
      setOpacity(1);
    }, 400);
    return () => clearTimeout(t);
  }, [src, displayedSrc]);

  if (failed && fallback) {
    return fallback;
  }

  return (
    <img
      src={displayedSrc}
      alt={alt}
      draggable={false}
      onError={() => setFailed(true)}
      data-testid="roboball-image"
      data-jersey-src={displayedSrc}
      className={`${className} ${imgClassName}`.trim()}
      style={{
        opacity,
        transition: "opacity 400ms ease-in-out",
        ...style,
      }}
      {...rest}
    />
  );
}
